import React, { useState, useMemo, useEffect } from "react";
import { Table, Select, InputNumber, Button, Input, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { BarChartOutlined, TableOutlined } from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { fieldDesc } from "@/constants/dataMap";
import userInfo from "@/Components/user";
import { updateActValue, createActValue } from "@/services/design";
import { rebuildFormula } from "@/config/func";
import "@/Components/DesignValue/DesignValue.less";
// 带折线图时的专属布局样式
import "./atc.less";

// 固定组分（全量物料）：列标题与运行时 molar_frac 的 key 一致（rebuildFormula 后的下标公式）
const FIXED_MATERIAL_LIST = [
  { name: "氢气", formula: "H2" },
  { name: "一氧化碳", formula: "CO" },
  { name: "二氧化碳", formula: "CO2" },
  { name: "甲烷", formula: "CH4" },
  { name: "氮气", formula: "N2" },
  { name: "氨气", formula: "NH3" },
  { name: "氰化氢", formula: "HCN" },
  { name: "硫化氢", formula: "H2S" },
  { name: "氧硫化碳", formula: "COS" },
  { name: "氩气", formula: "Ar" },
  { name: "水", formula: "H2O" },
  { name: "甲醇", formula: "CH3OH" },
];

// 流股指标：表格可编辑列与折线图指标共用同一份定义
const STREAM_METRICS = [
  { key: "f_kg_per_h", title: "质量流量（kg/h）", width: 130 },
  { key: "T_C", title: "温度（℃）", width: 100 },
  { key: "P_MPa", title: "压力（MPa）", width: 110 },
  { key: "f_nm3_per_h", title: "气相体积流量（Nm³/h）", width: 175 },
  { key: "f_m3_per_h", title: "液相体积流量（m³/h）", width: 160 },
  { key: "f_l_per_h", title: "液相体积流量（L/h）", width: 155 },
];

// 折线图配色：默认调色板仅 9 色，流股较多时会重复；用黄金角生成色相，保证各条线颜色互不相同
const CHART_COLORS = Array.from(
  { length: 60 },
  (_, i) => `hsl(${Math.round((i * 137.508) % 360)}, 65%, ${i % 2 === 0 ? 45 : 60}%)`,
);

// 只读格式化：无效值显示 "-"，有效值统一保留 6 位小数（可编辑表格不使用时保持原展示样式）
const formatNum = (v: any, _fixed: number = 6): string => {
  if (v === null || v === undefined || v === "" || isNaN(Number(v))) return "-";
  if (Number(v) === 0) return "0";
  return String(parseFloat(Number(v).toFixed(6)));
};

// 参数名 → 中文表头（t 温度带单位，pressure_drop 整塔压降带单位，其余优先 fieldDesc，未知回退原名）
const getParamTitle = (k: string): string => {
  if (k === "t") return "温度（℃）";
  if (k === "pressure_drop") return "整塔压降（MPa）";
  return fieldDesc[k] || k;
};

// 可编辑数字单元格：无边框输入框（只保留表格网格线），无效值显示为空，输入后回传数字
const EditableNum = ({ value, onChange, disabled }: { value?: any; onChange?: (v: any) => void; disabled?: boolean }) => {
  const isInvalid = value === null || value === undefined || value === "" || isNaN(Number(value));
  return (
    <InputNumber
      size="small"
      variant="borderless"
      style={{ width: "100%" }}
      value={isInvalid ? null : Number(value)}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

const ActValue = ({
  item,
  data,
  hourlyResults,
  onCreated,
}: {
  item?: any;
  data?: any;
  // 时间段查询时 realtime_hours 返回的按小时数据（[{ time, data: { input, output } }]），用于顶部折线图
  hourlyResults?: any[];
  // 在抽屉内创建工况成功后通知父级（传新建项，用于刷新列表并跳转到该项）
  onCreated?: (created?: { id?: any; name?: string }) => void;
}) => {
  console.log(item, data);
  // 查询到的时间点（realtime_hours 返回的整点）：页面只展示到小时，提交给接口仍用完整时间
  const hourlyTimeOptions = useMemo(
    () =>
      (hourlyResults || [])
        .map((r: any) => (r?.time ? dayjs(r.time) : null))
        .filter(Boolean)
        .map((t: any) => ({
          // 天作为分组层级展示，时间点只展示到小时
          day: t.format("YYYY-MM-DD"),
          label: t.format("HH:00"),
          value: t.format("YYYY-MM-DD HH:mm:ss"),
        })),
    [hourlyResults],
  );

  // 按天分组：左侧时间点列表加一层“天”的层级，跨天查询时可分组查看/勾选
  const dayGroups = useMemo(() => {
    const groups: { day: string; items: typeof hourlyTimeOptions }[] = [];
    hourlyTimeOptions.forEach((opt) => {
      const last = groups[groups.length - 1];
      if (last && last.day === opt.day) last.items.push(opt);
      else groups.push({ day: opt.day, items: [opt] });
    });
    return groups;
  }, [hourlyTimeOptions]);

  // 展开的天（新查询默认全部展开）
  const [expandedDays, setExpandedDays] = useState<string[]>([]);

  useEffect(() => {
    setExpandedDays(dayGroups.map((g) => g.day));
  }, [dayGroups]);

  // 表格查看的时间点：时间段查询时表格展示该时间点对应的实际值
  const [activeTime, setActiveTime] = useState("");

  // 每次新的时间段查询默认看第一个时间点
  useEffect(() => {
    setActiveTime(hourlyTimeOptions[0]?.value || "");
  }, [hourlyTimeOptions]);

  // 所选时间点那一小时的实际值
  const activeHourData = useMemo(() => {
    if (!activeTime) return null;
    const hit = (hourlyResults || []).find(
      (r: any) => r?.time && dayjs(r.time).format("YYYY-MM-DD HH:mm:ss") === activeTime,
    );
    return hit?.data || null;
  }, [hourlyResults, activeTime]);

  // 实际数据：时间段查询时取所选时间点的数据，否则取传入的 data（realtime 返回，格式 { input, output }）
  const actData = activeHourData || data || item?.values || null;
  const streams = actData?.output?.streams || actData?.values?.output?.streams || [];
  const elements = actData?.input?.elements || actData?.values?.input?.elements || [];

  const [selectedValue, setSelectedValue] = useState("stream");

  // 是否可编辑/保存：仅带存储记录（storeId）的实际值项可编辑；查询出来的数据保持原只读展示
  const editable = !!item?.storeId;

  // ============ 可编辑副本：数据来源变化时重建，编辑过程中不重置 ============
  const [editStreams, setEditStreams] = useState<any[]>([]);
  const [editElements, setEditElements] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditStreams(
      (streams || []).map((s: any) => ({
        ...s,
        molar_frac: (s.molar_frac || []).map((m: any) => ({ ...m })),
      })),
    );
    setEditElements(
      (elements || []).map((e: any) => ({ ...e, params: { ...(e.params || {}) } })),
    );
  }, [streams, elements]);

  // 组分列表：固定全量组分（不再依赖数据流股中出现过的组分）
  const materials = useMemo(
    () => FIXED_MATERIAL_LIST.map((m) => rebuildFormula(m.formula) || m.formula),
    [],
  );

  // 取某条流股某组分的摩尔分率
  const getMoleFrac = (s: any, matName: string) => {
    const mf = (s.molar_frac || []).find((m: any) => m[matName] !== undefined);
    return mf ? mf[matName] : null;
  };

  // 更新某条流股的普通数值字段（按行索引定位，避免同名 key 问题）
  const updateStreamField = (index: number, field: string, value: any) => {
    setEditStreams((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value ?? null } : row)),
    );
  };

  // 更新某条流股某组分的摩尔分率（原行缺少该组分时自动补上）
  const updateMoleFrac = (index: number, matName: string, value: any) => {
    setEditStreams((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const mf = (row.molar_frac || []).map((m: any) => ({ ...m }));
        const target = mf.find((m: any) => m[matName] !== undefined);
        if (target) target[matName] = value;
        else mf.push({ [matName]: value });
        return { ...row, molar_frac: mf };
      }),
    );
  };

  // 更新某单元操作的一个参数（按行索引定位）
  const updateElementParam = (index: number, key: string, value: any) => {
    setEditElements((prev) =>
      prev.map((e, i) =>
        i === index ? { ...e, params: { ...(e.params || {}), [key]: value ?? null } } : e,
      ),
    );
  };

  // ===================== 搜索流股 =====================
  const [highlightedName, setHighlightedName] = useState<string | null>(null);

  const streamOptions = useMemo(() => {
    return streams.map((s: any) => ({
      label: `📊 流股 - ${s.name}`,
      value: s.name,
    }));
  }, [streams]);

  // 只滚动表格自身的滚动容器：直接 scrollIntoView 会连带滚动抽屉/整页，把顶部区域推出去
  const scrollRowIntoView = (tr: HTMLElement) => {
    let scroller: HTMLElement | null = tr.parentElement;
    while (scroller && scroller !== document.body) {
      const oy = window.getComputedStyle(scroller).overflowY;
      if ((oy === "auto" || oy === "scroll") && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    if (!scroller || scroller === document.body) return;
    const trRect = tr.getBoundingClientRect();
    const boxRect = scroller.getBoundingClientRect();
    scroller.scrollTo({
      top: Math.max(
        0,
        scroller.scrollTop + (trRect.top - boxRect.top) - (scroller.clientHeight - trRect.height) / 2,
      ),
      behavior: "smooth",
    });
  };

  const handleSelect = (value: any) => {
    if (!value) {
      setHighlightedName(null);
      return;
    }
    if (selectedValue !== "stream") setSelectedValue("stream");
    // 折线图视图下没有流股行，先切回表格视图再定位
    setChartView("table");
    setHighlightedName(value);
    setTimeout(() => {
      const tr = document.querySelector(`tr[data-row-key="${value}"]`) as HTMLElement | null;
      if (tr) {
        scrollRowIntoView(tr);
        tr.classList.add("highlight-flash");
        setTimeout(() => {
          tr.classList.remove("highlight-flash");
        }, 2000);
      }
    }, 300);
  };

  // ===================== 保存：PUT /computes/{id}，result 整体回写 =====================
  const handleSave = async () => {
    if (!item?.storeId) {
      message.warning("当前数据没有存储记录，无法保存");
      return;
    }
    setSaving(true);
    try {
      const inputBase = actData?.input || actData?.values?.input || {};
      const outputBase = actData?.output || actData?.values?.output || {};
      const res = await updateActValue(
        item.storeId,
        {
          result: {
            input: { ...inputBase, elements: editElements },
            output: { ...outputBase, streams: editStreams },
          },
        },
        userInfo.access_token,
      );
      console.log("保存实际值:", res.data);
      message.success("保存成功");
    } catch (err) {
      console.error("保存实际值失败:", err);
      message.error("保存失败");
    } finally {
      setSaving(false);
    }
  };

  // ===================== 流股表（可编辑） =====================
  const basicStreamColumns: ColumnsType<any> = [
    { title: "流股", dataIndex: "name", key: "name", align: "center", width: 100, fixed: "left" },
    ...STREAM_METRICS.map((f) => ({
      title: f.title,
      dataIndex: f.key,
      key: f.key,
      align: "center" as const,
      width: f.width,
      render: (v: any, r: any, index: number) =>
        editable ? (
          <EditableNum
            value={r[f.key]}
            onChange={(nv) => updateStreamField(index, f.key, nv)}
          />
        ) : (
          <span>{formatNum(r[f.key])}</span>
        ),
    })),
  ];

  const materialColumns: ColumnsType<any> = materials.map((name, colIdx) => ({
    title: name,
    key: `mat-${name}-${colIdx}`,
    align: "center" as const,
    width: 110,
    render: (_: any, r: any, index: number) =>
      editable ? (
        <EditableNum
          value={getMoleFrac(r, name)}
          onChange={(nv) => updateMoleFrac(index, name, nv)}
        />
      ) : (
        <span>{formatNum(getMoleFrac(r, name))}</span>
      ),
  }));

  // ===================== 单元操作表（可编辑） =====================
  const paramKeys = useMemo(() => {
    const keys: string[] = [];
    elements.forEach((e: any) => {
      Object.keys(e.params || {}).forEach((k) => {
        if (!keys.includes(k)) keys.push(k);
      });
    });
    return keys;
  }, [elements]);

  // ===================== 按小时折线图（仅时间段查询时有数据） =====================
  const [chartMetric, setChartMetric] = useState("");
  // 选中的流股/设备（空数组表示展示全部）
  const [chartRows, setChartRows] = useState<string[]>([]);
  // 有折线图时：折线图与表格各占满一屏，用这个切换查看（默认表格）
  const [chartView, setChartView] = useState<"chart" | "table">("table");

  // 数值转换：无效值统一成 null，折线图断开该点
  const toNum = (v: any) => (v === null || v === undefined || isNaN(Number(v)) ? null : Number(v));

  // 把每小时的数据整理成：times 时间轴 + 指标 -> { 行名 -> 值数组 }
  const hourlyChartData = useMemo(() => {
    const results = hourlyResults || [];
    if (!results.length) return null;
    // 页面上的时间只展示到小时（数据本身是整点）
    const times = results.map((r: any) =>
      r?.time ? dayjs(r.time).format("YYYY-MM-DD HH:00") : r?.time,
    );

    const streamSeries: Record<string, Record<string, (number | null)[]>> = {};
    STREAM_METRICS.forEach((m) => (streamSeries[m.key] = {}));
    results.forEach((r: any, ti: number) => {
      (r.data?.output?.streams || []).forEach((s: any) => {
        if (!s?.name) return;
        STREAM_METRICS.forEach((m) => {
          const row = streamSeries[m.key];
          if (!row[s.name]) row[s.name] = new Array(times.length).fill(null);
          row[s.name][ti] = toNum(s[m.key]);
        });
      });
    });

    const elementSeries: Record<string, Record<string, (number | null)[]>> = {};
    results.forEach((r: any, ti: number) => {
      (r.data?.input?.elements || []).forEach((e: any) => {
        if (!e?.name) return;
        Object.entries(e.params || {}).forEach(([k, v]) => {
          if (!elementSeries[k]) elementSeries[k] = {};
          if (!elementSeries[k][e.name]) elementSeries[k][e.name] = new Array(times.length).fill(null);
          elementSeries[k][e.name][ti] = toNum(v);
        });
      });
    });

    return { times, streamSeries, elementSeries };
  }, [hourlyResults]);

  // 指标下拉：流股用固定指标，单元操作用数据里出现的参数
  const chartMetricOptions = useMemo(() => {
    if (!hourlyChartData) return [];
    if (selectedValue === "stream") {
      return STREAM_METRICS.map((m) => ({ label: m.title, value: m.key }));
    }
    return Object.keys(hourlyChartData.elementSeries).map((k) => ({
      label: getParamTitle(k),
      value: k,
    }));
  }, [hourlyChartData, selectedValue]);

  // 行下拉：当前指标下有哪些流股 / 设备（该指标整条都没有数据的行不参与）
  const chartRowOptions = useMemo(() => {
    if (!hourlyChartData || !chartMetric) return [];
    const matrix =
      selectedValue === "stream"
        ? hourlyChartData.streamSeries[chartMetric]
        : hourlyChartData.elementSeries[chartMetric];
    return Object.keys(matrix || {})
      .filter((name) => (matrix[name] || []).some((v) => v !== null && v !== undefined))
      .map((name) => ({ label: name, value: name }));
  }, [hourlyChartData, chartMetric, selectedValue]);

  // 切换流股/单元操作或数据变化时，重置为第一个指标并展示全部行
  useEffect(() => {
    if (!hourlyChartData) {
      setChartMetric("");
      setChartRows([]);
      return;
    }
    const first =
      selectedValue === "stream"
        ? STREAM_METRICS[0]?.key
        : Object.keys(hourlyChartData.elementSeries)[0];
    setChartMetric(first || "");
    setChartRows([]);
  }, [hourlyChartData, selectedValue]);

  // 每次新的时间段查询（按小时数据变化）默认先看表格
  useEffect(() => {
    if (hourlyResults?.length) setChartView("table");
  }, [hourlyResults]);

  // ===================== 创建工况数据（用查询到的时间点） =====================
  const [createName, setCreateName] = useState("");
  // 勾选的时间点，提交时作为 times
  const [createTimes, setCreateTimes] = useState<string[]>([]);
  const [createLoading, setCreateLoading] = useState(false);

  // 每次新的时间段查询：时间点默认全选，名称重置
  useEffect(() => {
    setCreateTimes(hourlyTimeOptions.map((o) => o.value));
    setCreateName("");
  }, [hourlyTimeOptions]);

  const handleCreateAct = async () => {
    if (!createName.trim()) {
      message.warning("请输入工况名称");
      return;
    }
    if (!createTimes.length) {
      message.warning("请选择时间点");
      return;
    }
    setCreateLoading(true);
    try {
      const name = createName.trim();
      const res = await createActValue({ name, times: createTimes }, userInfo.access_token);
      console.log("创建工况数据:", res.data);
      message.success("创建成功");
      setCreateName("");
      // 通知父级刷新列表并跳转到新建的这一项
      onCreated?.({ id: res.data?.id, name });
    } catch (err) {
      console.error("创建工况数据失败:", err);
      message.error("创建失败");
    } finally {
      setCreateLoading(false);
    }
  };

  const chartOption = useMemo(() => {
    if (!hourlyChartData || !chartMetric) return null;
    const matrix =
      selectedValue === "stream"
        ? hourlyChartData.streamSeries[chartMetric]
        : hourlyChartData.elementSeries[chartMetric];
    if (!matrix) return null;
    // 该指标整条都没有数据的流股/设备不画线，也不占用颜色
    const validNames = Object.keys(matrix).filter((name) =>
      (matrix[name] || []).some((v) => v !== null && v !== undefined),
    );
    const names = chartRows.length ? validNames.filter((n) => chartRows.includes(n)) : validNames;
    if (!names.length) return null;
    return {
      color: CHART_COLORS,
      tooltip: { trigger: "axis" },
      legend: { type: "scroll", top: 0 },
      // containLabel: 纵轴刻度文字较长（如质量流量、L/h）时自动留出足够宽度，避免被裁剪
      grid: { left: 12, right: 24, top: 40, bottom: 56, containLabel: true },
       xAxis: {
        type: "category",
        data: hourlyChartData.times,
        boundaryGap: false,
        axisLabel: {
          rotate: 40,
          // 完整时间为 "YYYY-MM-DD HH:mm:ss"，仅展示 "MM-DD HH:mm" 避免过长
          formatter: (v: string) => (v ? v.slice(5, 16) : v),
          hideOverlap: true,
        },
      },
      // xAxis: {
      //   type: "category",
      //   data: hourlyChartData.times,
      //   boundaryGap: false,
      //   axisLabel: { rotate: 30 },
      // },
      yAxis: { type: "value", scale: true },
      series: names.map((name) => ({
        name,
        type: "line",
        smooth: true,
        connectNulls: true,
        data: matrix[name],
      })),
    };
  }, [hourlyChartData, chartMetric, chartRows, selectedValue]);

  const elementColumns: ColumnsType<any> = [
    { title: "设备名称", dataIndex: "name", key: "name", align: "center", width: 120, fixed: "left" },
    ...paramKeys.map((k) => {
      // 若某行该参数是对象/数组（复杂结构），保持只读 JSON 展示；数值类可编辑
      const complexValue = (r: any) => {
        const v = r.params?.[k];
        return v !== null && v !== undefined && (Array.isArray(v) || typeof v === "object");
      };
      return {
        title: getParamTitle(k),
        key: `param-${k}`,
        align: "center" as const,
        render: (_: any, r: any, index: number) =>
          complexValue(r) ? (
            <span style={{ fontSize: 12, color: "#666" }}>{JSON.stringify(r.params?.[k])}</span>
          ) : editable ? (
            <EditableNum
              value={r.params?.[k]}
              onChange={(nv) => updateElementParam(index, k, nv)}
            />
          ) : (
            <span>{formatNum(r.params?.[k])}</span>
          ),
      };
    }),
  ];

  if (!actData) {
    return <div style={{ padding: 20, color: "#999" }}>暂无实际数据</div>;
  }

  // 折线图与表格各占满一屏：表格高度统一用满屏计算值
  const hasChart = !!chartOption;
  const tableScroll = {
    x: "max-content" as const,
    y: "calc(100vh - 280px)",
  };

  // 勾选/取消某个时间点（创建工况用的多选）
  const toggleCreateTime = (time: string) => {
    setCreateTimes((prev) =>
      prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time],
    );
  };

  // 时间点全选/全不选
  const handleSelectAllTimes = () => {
    setCreateTimes((prev) =>
      prev.length === hourlyTimeOptions.length ? [] : hourlyTimeOptions.map((o) => o.value),
    );
  };

  // 展开的天（新查询默认全部展开）：state 与 effect 已上移到提前 return 之前
  const toggleDay = (day: string) => {
    setExpandedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  // 勾选/取消某一天（即该天全部时间点）
  const toggleCreateDay = (items: { value: string }[]) => {
    const values = items.map((o) => o.value);
    const allChecked = values.every((v) => createTimes.includes(v));
    setCreateTimes((prev) =>
      allChecked
        ? prev.filter((t) => !values.includes(t))
        : Array.from(new Set([...prev, ...values])),
    );
  };

  // 流股/单元操作表格：时间段查询时放在右侧（左侧是时间点列表），其它情况直接铺满内容区
  const tablesContent =
    selectedValue === "element" ? (
      <div className="elements-table-container">
        <Table
          rowKey="name"
          columns={elementColumns}
          dataSource={editable ? editElements : elements}
          pagination={false}
          bordered
          className="elements-table"
          scroll={tableScroll}
        />
      </div>
    ) : (
      <Table
        rowKey="name"
        columns={[
          ...basicStreamColumns,
          ...(materials.length > 0
            ? [{ title: "组分（摩尔分率）", children: materialColumns }]
            : []),
        ]}
        dataSource={editable ? editStreams : streams}
        pagination={false}
        bordered
        className="merge-table"
        rowClassName={(record: any) => (highlightedName === record.name ? "highlight-row" : "")}
        scroll={tableScroll}
      />
    );
 

  return (
    <div
      className={`design-value-container${hasChart ? " with-chart" : ""}`}
      style={{ height: "100%" }}
    >
      <div className="fixed-header">
        <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start" }}>
          <div className="tab-button-group" style={{ display: "flex", alignItems: "center" }}>
            <span style={{ fontSize: "20px", fontWeight: "bold", lineHeight: "34px", marginRight: "10px" }}>{item?.label}：</span>
            <button
              className={`tab-button ${selectedValue === "element" ? "active" : ""}`}
              onClick={() => setSelectedValue("element")}
            >
              <span className={`tab-button-text ${selectedValue === "element" ? "active" : ""}`}>单元操作</span>
            </button>
            <button
              className={`tab-button ${selectedValue === "stream" ? "active" : ""}`}
              onClick={() => setSelectedValue("stream")}
            >
              <span className={`tab-button-text ${selectedValue === "stream" ? "active" : ""}`}>流股</span>
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 14, color: "#666" }}>搜索流股：</span>
            <Select
              style={{ width: 280 }}
              dropdownStyle={{ zIndex: 100003 }}
              placeholder="搜索流股"
              showSearch
              options={streamOptions}
              onChange={handleSelect}
              allowClear
              filterOption={(input, option) => {
                const label = String(option?.label || "");
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </div>

          {/* 右侧：保存按钮 + 折线图/表格切换（切换按钮贴整行最右端） */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            {editable && (
              <Button
                type="primary"
                loading={saving}
                onClick={handleSave}
                style={{
                  background: "#52c41a",
                  borderColor: "#52c41a",
                }}
              >
                保存
              </Button>
            )}
            {hasChart && (
              // 与 Distribution 页的图表/表格切换按钮保持一致
              <div className="hourly-view-switch">
                <button
                  className={`view-type-btn chart-btn ${chartView === "chart" ? "active" : ""}`}
                  onClick={() => setChartView("chart")}
                  title="折线图"
                >
                  <BarChartOutlined
                    style={{ fontSize: 16, color: chartView === "chart" ? "#fff" : "#bebebe" }}
                  />
                </button>
                <button
                  className={`view-type-btn table-btn ${chartView === "table" ? "active" : ""}`}
                  onClick={() => setChartView("table")}
                  title="表格"
                >
                  <TableOutlined
                    style={{ fontSize: 16, color: chartView === "table" ? "#fff" : "#bebebe" }}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="scrollable-content">
        {/* 时间段查询时：折线图 / 表格 各占满一屏，切换查看 */}
        {chartOption && chartView === "chart" ? (
          <div className="hourly-chart-box">
            <div className="hourly-chart-toolbar">
              <span className="hourly-chart-label">折线图指标：</span>
              <Select
                size="small"
                style={{ width: 220 }}
                value={chartMetric || undefined}
                options={chartMetricOptions}
                onChange={(v) => setChartMetric(v)}
              />
              <span className="hourly-chart-label">
                {selectedValue === "stream" ? "流股：" : "设备："}
              </span>
              <Select
                size="small"
                mode="multiple"
                style={{ flex: 1, minWidth: 260 }}
                placeholder="默认全部"
                maxTagCount="responsive"
                allowClear
                value={chartRows}
                options={chartRowOptions}
                onChange={(v) => setChartRows(v)}
              />
            </div>
            <ReactECharts
              className="hourly-chart"
              style={{ height: "100%" }}
              option={chartOption}
              notMerge
              lazyUpdate
            />
          </div>
        ) : hourlyTimeOptions.length > 0 ? (
          // 时间段查询的表格：左侧时间点列表（点击高亮查看，可多选用于创建工况），右侧展示所选时间点的数据
          <div className="time-table-layout">
            <div className="time-list-sidebar">
              <div className="time-list-header">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={
                      hourlyTimeOptions.length > 0 &&
                      createTimes.length === hourlyTimeOptions.length
                    }
                    onChange={handleSelectAllTimes}
                  />
                  <span>全选</span>
                </label>
                <Input
                  size="small"
                  placeholder="运行数据名称"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  maxLength={50}
                />
                <Button
                  size="small"
                  type="primary"
                  loading={createLoading}
                  onClick={handleCreateAct}
                  block
                >
                  按勾选时间创建运行数据
                </Button>
              </div>
              <div className="time-list">
                {dayGroups.map((group) => (
                  <div className="time-day-group" key={group.day}>
                    {/* 天层级：点击展开/收起，勾选框选中该天全部时间点 */}
                    <div
                      className="time-day-header"
                      onClick={() => toggleDay(group.day)}
                    >
                      <input
                        type="checkbox"
                        checked={group.items.every((o) => createTimes.includes(o.value))}
                        onChange={() => toggleCreateDay(group.items)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="day-name">{group.day}</span>
                      <span
                        className={`day-arrow ${
                          expandedDays.includes(group.day) ? "expanded" : ""
                        }`}
                      >
                        ▸
                      </span>
                    </div>
                    {expandedDays.includes(group.day) && (
                      <div className="time-day-items">
                        {group.items.map((opt) => (
                          <div
                            key={opt.value}
                            className={`time-item ${activeTime === opt.value ? "active" : ""}`}
                            onClick={() => setActiveTime(opt.value)}
                          >
                            <input
                              type="checkbox"
                              checked={createTimes.includes(opt.value)}
                              onChange={() => toggleCreateTime(opt.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="time-name">{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="time-table-content">{tablesContent}</div>
          </div>
        ) : (
          tablesContent
        )}
      </div>
    </div>
  );
};

export default ActValue;
